import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Grid, useTheme, Paper } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';

/**
 * Редактор видео контента для добавления видео по URL
 * @param {string} content - JSON строка с данными видео (videoUrl)
 * @param {function} onChange - Функция обработки изменений
 */
const defaultData = {
  videoUrl: '',
};
const VideoContentEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons', 'common']);
  // Начальные данные для видео контента

  // Состояние для видео данных
  const [videoData, setVideoData] = useState(defaultData);
  // Отслеживаем изменения
  const [isDirty, setIsDirty] = useState(false);
  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

  const theme = useTheme();

  // Синхронизируем локальное состояние с пропсами при их изменении
  useEffect(() => {
    try {
      const parsedData = content ? JSON.parse(content) : defaultData;
      setVideoData(parsedData);
      setIsDirty(false);
    } catch (e) {
      console.error(t('lessons:editor.video.parseError'), e);
      setVideoData(defaultData);
    }
  }, [content, t]);

    // Сохраняем изменения в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty) {
      onChange(JSON.stringify(videoData));
      setIsDirty(false);
    }
  }, [videoData, isDirty, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveChangesToParent();
      }, 1000);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [videoData, isDirty, saveChangesToParent]);

  // Обработчик изменения URL видео
  const handleVideoUrlChange = (e) => {
    setVideoData((prev) => ({ ...prev, videoUrl: e.target.value }));
    setIsDirty(true);
  };

  // Функция для получения встраиваемого URL на основе обычного URL
  const getEmbedUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/i;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Если не удалось преобразовать URL, возвращаем исходный
    return url;
  };

  // Проверяем, является ли URL действительным URL видео
  const isValidVideoUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be|vimeo\.com|mp4$|webm$|ogg$/i.test(url);
  };

  // Рендерим предпросмотр видео
  const renderVideoPreview = () => {
    const embedUrl = getEmbedUrl(videoData.videoUrl);

    if (!isValidVideoUrl(videoData.videoUrl)) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.secondary,
            borderRadius: '16px',
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <PlayCircleOutlineIcon fontSize="large" />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('lessons:editor.video.enterValidUrl')}
          </Typography>
        </Paper>
      );
    }
      return (
      <Box
        sx={{ position: 'relative', width: '100%', pb: '56.25%', height: 0 }}
      >
        <iframe
          src={embedUrl}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '16px',
          }}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={t('lessons:editor.video.previewTitle')}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3}>
        {/* Секция редактирования URL видео */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('lessons:editor.video.urlLabel')}
            placeholder={t('lessons:editor.video.urlPlaceholder')}
            value={videoData.videoUrl || ''}
            onChange={handleVideoUrlChange}
            onBlur={saveChangesToParent}
            helperText={t('lessons:editor.video.urlHelper')}
          />
        </Grid>

        {/* Предпросмотр видео */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: '16px',
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              {t('lessons:editor.video.preview')}
            </Typography>
            {renderVideoPreview()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VideoContentEditor;
